import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';

import i18n from 'i18next';

import keycloak from '../../keycloak';
import LocalStorageWrapper from '../../core/wrappers/browserStorage/localStorage-wrapper';
import configureStore from '../../stores/redux/configureStore';
import { StoreActionType } from '../../stores/redux/types';
import { setCheckOperatorInfo, setCurrentUser } from '../../stores/redux/actions';
import { PageUrl } from '../../types/enums';
import Helper from '../../utils/helper';
import { GeneralQuery } from '../query';
import { getLoginRedirectUri, getLogoutRedirectUri } from './authRedirect';

let keycloakHooksRegistered = false;

export default class AuthService {
    private isFinalizingLogin = false;

    constructor() {
        if (keycloakHooksRegistered) {
            return;
        }
        keycloakHooksRegistered = true;

        keycloak.onTokenExpired = () => {
            console.log('token expired - re-login required');
            this.logout();
        };
    }

    private resolveOperatorErrorMessage(messageCode?: string): string {
        if (messageCode) {
            const key = `errors.${messageCode}`;
            if (i18n.exists(key)) {
                return i18n.t(key);
            }
        }
        return i18n.t('general.userContextMissing');
    }

    private async handleUserLoaded() {
        if (keycloak.token) {
            localStorage.setItem('access_token', keycloak.token);

            const exp = keycloak.tokenParsed?.exp;
            if (exp) localStorage.setItem('token_expires', String(exp * 1000));

            const langClaim = undefined;

            if (langClaim === '2') localStorage.setItem('lan', 'en-US');
            else if (langClaim === '1') localStorage.setItem('lan', 'tr-TR');
            else localStorage.setItem('lan', langClaim || 'tr-TR');

            if (keycloak.idToken) {
                localStorage.setItem('id_token', keycloak.idToken);
            }
            if (keycloak.refreshToken) {
                localStorage.setItem('refresh_token', keycloak.refreshToken);
            }
        }
    }

    private applyUserProfile(accessToken: string): void {
        try {
            const decodeToken: any = jwt_decode(accessToken);
            LocalStorageWrapper.setItemToLocalStorage('userName', decodeToken?.name);
            setCurrentUser(configureStore().dispatch)({
                email: decodeToken?.email,
                name: decodeToken?.name,
                accessToken,
            });
        } catch (error) {
            console.error('[Auth] Failed to decode access token', error);
        }
    }

    /**
     * Keycloak login callback sonrası: check-operator, returnPath yönlendirme.
     * (Eski /signin-oidc Login bileşeni yerine.)
     */
    async finalizeSessionAfterLogin(): Promise<void> {
        if (this.isFinalizingLogin || !keycloak.token) {
            return;
        }

        this.isFinalizingLogin = true;
        try {
            await this.handleUserLoaded();

            const result = await this.completeLogin();
            if (!result?.token?.access_token) {
                return;
            }

            const checkOperatorInfo: any = result.checkOperatorInfo;
            const operatorFailed = checkOperatorInfo?.isSuccess === false || checkOperatorInfo?.IsSuccess === false;

            if (operatorFailed) {
                await Swal.fire({
                    icon: 'warning',
                    title: i18n.t('general.userContextMissingTitle'),
                    text: this.resolveOperatorErrorMessage(checkOperatorInfo?.message || checkOperatorInfo?.Message),
                    confirmButtonText: i18n.t('general.userContextMissingConfirm'),
                });
                this.logout();
                return;
            }

            if (checkOperatorInfo?.data || checkOperatorInfo?.Data) {
                await setCheckOperatorInfo(configureStore().dispatch)(checkOperatorInfo.data || checkOperatorInfo.Data);
            }

            this.applyUserProfile(result.token.access_token);

            let returnPath: any = LocalStorageWrapper.getItemFromLocalStorage('returnPath');
            if (Helper.isNil(returnPath)) {
                returnPath = PageUrl.home.url;
            } else {
                LocalStorageWrapper.removeItemFromLocalStorage('returnPath');
            }

            const currentPath = window.location.pathname + window.location.search;
            const hasOauthCallbackParams =
                window.location.search.includes('code=') ||
                window.location.search.includes('state=') ||
                window.location.hash.includes('code=');

            if (hasOauthCallbackParams || currentPath !== returnPath) {
                window.location.replace(returnPath);
            }
        } finally {
            this.isFinalizingLogin = false;
        }
    }

    async getAccessToken(): Promise<string | null> {
        if (keycloak.authenticated && keycloak.token) {
            return keycloak.token;
        }
        return localStorage.getItem('access_token');
    }

    async getUser(): Promise<string | null> {
        return this.getAccessToken();
    }

    getUserInfo = async () => {
        const token = await this.getAccessToken();
        if (token) {
            return {
                access_token: token,
                id_token: localStorage.getItem('id_token'),
                profile: keycloak.tokenParsed,
                expired: false,
            };
        }
        return null;
    };

    login = (): void => {
        keycloak.login({
            redirectUri: getLoginRedirectUri(),
        });
    };

    async completeLogin(): Promise<any> {
        try {
            const checkOperator: any = await GeneralQuery.GetCheckOperatorList();

            if (checkOperator?.isSuccess === false || checkOperator?.IsSuccess === false) {
                return {
                    checkOperatorInfo: checkOperator,
                    token: {
                        access_token: keycloak.token,
                        id_token: keycloak.idToken,
                        profile: keycloak.tokenParsed,
                    },
                };
            }

            const operatorData = checkOperator?.data || checkOperator?.Data || {};

            localStorage.setItem('checkOperator', JSON.stringify(operatorData));
            localStorage.setItem('isStoreEmployee', JSON.stringify(operatorData?.isStoreEmployee));
            localStorage.setItem('storeCode', operatorData?.storeCode);

            configureStore().dispatch({
                type: StoreActionType.SET_CHECK_OPERATOR_INFO,
                info: operatorData,
            });

            return {
                checkOperatorInfo: checkOperator,
                token: {
                    access_token: keycloak.token,
                    id_token: keycloak.idToken,
                    profile: keycloak.tokenParsed,
                },
            };
        } catch (err: any) {
            console.error('[Keycloak Error]: cl', err);
            return null;
        }
    }

    refreshToken = async () => {
        return;
    };

    logout = (): void => {
        LocalStorageWrapper.clearLocalStorageExcept(['columnVisibilityModel', 'headerSearch', 'returnListTour']);
        keycloak.logout({
            redirectUri: getLogoutRedirectUri(),
        });
    };

    completeLogout = () => {
        LocalStorageWrapper.clearLocalStorageExcept(['columnVisibilityModel', 'headerSearch', 'returnListTour']);
        return Promise.resolve();
    };
}
